import { useRef } from "react";
import { useConfirmDialogFocus } from "@/components/shared/useConfirmDialogFocus";

type DeleteConfirmModalProps = {
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Specific noun for the record being deleted, e.g. "Child record",
   * "Family record", "Staff record". When provided, drives the title and
   * message so both match the wording used by BlockedDeletionDialog's
   * internal final-confirm step.
   *
   * If omitted, the modal falls back to generic "this record" copy so
   * call sites that haven't been migrated still render correctly.
   */
  recordKindLabel?: string;
  /**
   * Optional escape hatches. If you pass either, it overrides whatever
   * would have been derived from recordKindLabel. Avoid unless you need
   * non-deletion wording for some reason.
   */
  title?: string;
  message?: React.ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  fallbackFocusRef?: React.RefObject<HTMLElement | null>;
};

export function DeleteConfirmModal({
  onCancel,
  onConfirm,
  recordKindLabel,
  title,
  message,
  confirmButtonText = "Delete",
  cancelButtonText = "Cancel",
  fallbackFocusRef,
}: DeleteConfirmModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const handleKeyDown = useConfirmDialogFocus(
    cancelRef,
    confirmRef,
    onCancel,
    fallbackFocusRef
  );

  const resolvedTitle =
    title ??
    (recordKindLabel ? `Delete ${recordKindLabel}?` : "Delete this record?");

  const resolvedMessage =
    message ??
    (recordKindLabel
      ? `This permanently deletes the ${recordKindLabel.toLowerCase()}. This action cannot be undone.`
      : "This permanently deletes the record. This action cannot be undone.");

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-confirm-title"
        aria-describedby="delete-confirm-text"
        style={{
          background: "white",
          padding: "32px 40px",
          borderRadius: "8px",
          maxWidth: "480px",
          width: "90%",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
      >
        <h5
          id="delete-confirm-title"
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#333",
            marginBottom: "12px",
            textAlign: "left",
            margin: "0 0 12px 0",
          }}
        >
          {resolvedTitle}
        </h5>
        <p
          id="delete-confirm-text"
          style={{
            fontSize: "14px",
            color: "#666",
            marginBottom: "24px",
            lineHeight: "1.5",
            textAlign: "left",
            margin: "0 0 24px 0",
          }}
        >
          {resolvedMessage}
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <button
            ref={cancelRef}
            onClick={onCancel}
            onKeyDown={handleKeyDown}
            style={{
              padding: "8px 20px",
              background: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              color: "#333",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            {cancelButtonText}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            onKeyDown={handleKeyDown}
            style={{
              padding: "8px 20px",
              background: "#dc3545",
              border: "none",
              borderRadius: "4px",
              color: "white",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            {confirmButtonText}
          </button>
        </div>
      </div>
    </div>
  );
}
